import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Paper,
    Box,
    Container,
    Grid,
    Divider,
    Typography,
    Alert,
    Skeleton,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
    SttLabelForm,
    SttButton,
    SttLabelDate,
    SttButtonTicket,
} from '../../../sporttia/all';
import useTpvsService from '../../../services/TpvsService';
import { useInteractionsFiles, useLoader } from '../../../lib/hooks';
import { formatPriceByLocale } from '../../../lib/utils';
import constants from '../../../config/constants';

const maxAttempts = 20;

// Types are inferred from API response

/**
 *  View with the result of a payment made with the virtual POS.
 */
export default function TPVResult(): React.ReactElement {
    const { t } = useTranslation();
    const { id: tpvId } = useParams<{ id: string }>();
    const tpvsService = useTpvsService();
    const { openFile } = useInteractionsFiles();
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [skeletonOff, setSkeletonOff] = useState(false);
    const [refetchInterval, setRefetchInterval] = useState<number | false>(
        1000,
    );
    const [refetchAttempts, setRefetchAttempts] = useState(1);
    const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);

    const tpvQuery = tpvsService.useGetTpv(
        tpvId ? parseInt(tpvId, 10) : 0,
        { trash: true },
        {
            enabled: tpvId !== undefined && tpvId !== null,
            refetchInterval,
        } as any,
    );

    useEffect(() => {
        if (tpvQuery.data?.tpv) {
            const tpv = tpvQuery.data.tpv;
            if (refetchAttempts < maxAttempts) {
                setRefetchAttempts((prev: any) => prev + 1);

                // Condiciones por las que un tpv se está confirmando
                if (
                    (tpv.status === 'CREATING' || tpv.status === 'PAYING') &&
                    tpv.result === 'RESULT_NOT_PAID'
                ) {
                    setIsConfirmingPayment(true);
                    setSkeletonOff(true);
                    return;
                }

                setIsConfirmingPayment(false);

                if (tpv.result === 'RESULT_OK' && tpv.status === 'PAID') {
                    setRefetchInterval(false);
                    setPaymentSuccess(true);
                    setSkeletonOff(true);
                } else {
                    setRefetchInterval(false);
                    setSkeletonOff(true);
                }
            } else {
                setIsConfirmingPayment(false);
                setRefetchInterval(false);
                setSkeletonOff(true);
            }
        }
    }, [tpvQuery.data]);

    const [, loader] = useLoader([
        tpvQuery.status ? tpvQuery.status : 'loading',
    ]);

    /**
     * Download ticket if payment was successful.
     */
    const downloadTicket = () => {
        openFile(
            `/payments/${(tpvQuery?.data?.tpv?.payment as any)?.id}.pdf`,
            'application/pdf',
            null,
            `${t('labels.ticket')}_${(tpvQuery?.data?.tpv?.payment as any)?.id}`,
        );
    };

    let paymentText;
    if (paymentSuccess) {
        paymentText = (
            <>
                <h2>{t('tpv.paymentCollected')}</h2>
                <Typography color="textSecondary" variant="body2">
                    {t('page.tpv.ok.paymentOk')}
                </Typography>
            </>
        );
    } else if (tpvQuery.data?.tpv.result === constants.tpv.results.errorPurse) {
        paymentText = (
            <>
                <h1>{t('tpv.error.title')}</h1>
                <Typography color="textSecondary" variant="body2">
                    {t('tpv.errorPurseRefund')}
                </Typography>
            </>
        );
    } else if (isConfirmingPayment) {
        paymentText = (
            <>
                <h1>{t('tpv.confirmingPayment')}</h1>
                <Typography color="textSecondary" variant="body2">
                    {t('tpv.waitForPaymentConfirmation')}
                </Typography>
            </>
        );
    } else {
        paymentText = (
            <>
                <h1>{t('tpv.error.title')}</h1>
                <Typography color="textSecondary" variant="body2">
                    {t('tpv.error.unknown.desc')}
                </Typography>
            </>
        );
    }

    return (
        <Box my={5}>
            {loader}
            {skeletonOff ? (
                <Container maxWidth="sm">
                    <Box my={2}>
                        <Grid container>
                            {!isConfirmingPayment && (
                                <Grid size={{ xs: 6 }}>
                                    <SttButton
                                        icon={<ArrowBackIcon />}
                                        caption={t('tpv.backToSporttia')}
                                        onClick={() => {
                                            window.parent.location.href = '/';
                                        }}
                                    />
                                </Grid>
                            )}
                            {paymentSuccess && (
                                <Grid size={{ xs: 6 }}>
                                    <SttButtonTicket
                                        fullWidth
                                        onClick={downloadTicket}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Box>

                    <Paper>
                        <Box p={3}>
                            {paymentText}
                            <Box my={4}>
                                <Divider variant="middle" />
                            </Box>

                            {/* --- Error, money on the e-purse --- */}
                            {tpvQuery?.data?.tpv?.result ===
                                'RESULT_ERROR_MONEY_IN_BANK' && (
                                <Box my={5}>
                                    <Alert severity="error">
                                        {t('page.tpv.ok.moneyInBank')}
                                    </Alert>
                                </Box>
                            )}

                            {/* --- TPV data --- */}
                            <Grid container spacing={3}>
                                <SttLabelForm
                                    grid
                                    md={4}
                                    sm={6}
                                    xs={6}
                                    title={t('payment.invoiceNumber')}
                                    text={tpvQuery?.data?.tpv?.id?.toString()}
                                />

                                <SttLabelForm
                                    grid
                                    md={4}
                                    sm={6}
                                    xs={6}
                                    title={t('general.created')}
                                    text={
                                        tpvQuery?.data?.tpv?.created ? (
                                            <SttLabelDate
                                                date={tpvQuery.data.tpv.created}
                                            />
                                        ) : undefined
                                    }
                                />

                                <SttLabelForm
                                    grid
                                    md={4}
                                    sm={6}
                                    xs={6}
                                    title={t('payment.paymentForm')}
                                    text={t('payment.creditCard')}
                                />

                                <SttLabelForm
                                    grid
                                    md={12}
                                    sm={12}
                                    xs={12}
                                    title={t('payment.concept')}
                                    text={tpvQuery?.data?.tpv?.concept}
                                />

                                <SttLabelForm
                                    grid
                                    md={12}
                                    sm={12}
                                    xs={12}
                                    title={t('sportcenter.sportcenter')}
                                    text={
                                        (tpvQuery?.data?.tpv?.sc as any)?.name
                                    }
                                />

                                <SttLabelForm
                                    grid
                                    md={10}
                                    sm={12}
                                    xs={12}
                                    title={t('tpv.payer')}
                                    text={
                                        (tpvQuery?.data?.tpv?.user as any)?.name
                                    }
                                />

                                <SttLabelForm
                                    grid
                                    md={2}
                                    sm={12}
                                    xs={12}
                                    title={t('labels.price')}
                                    text={`${formatPriceByLocale(
                                        tpvQuery?.data?.tpv?.price,
                                        (tpvQuery?.data?.tpv as any)?.priceShape
                                            ?.currency,
                                        (tpvQuery?.data?.tpv as any)?.priceShape
                                            ?.locale,
                                    )}`}
                                />
                            </Grid>
                        </Box>
                    </Paper>
                </Container>
            ) : (
                <Container maxWidth="sm">
                    <Skeleton variant="rectangular" height="50vh" />
                </Container>
            )}
        </Box>
    );
}
